// Map a Supabase error (Postgres + GoTrue auth) to a user-friendly string.
//
// Most callers do `try { await service.x() } catch (err) { setError(err.message) }`,
// which surfaces raw Postgres text like "duplicate key value violates unique
// constraint \"users_email_key\"". This util produces sentences a user can act on.
//
// `context` is optional; provide it when the same code (e.g. 23505) means
// something specific in a specific surface. Without context we fall back to a
// generic-but-clean message rather than the raw DB text.

export function mapSupabaseError(err, context = {}) {
  if (!err) return 'Something went wrong.';
  const code = err.code || '';
  const msg = err.message || '';

  // Postgres error codes (https://www.postgresql.org/docs/current/errcodes-appendix.html)
  if (code === '23505') {
    return context.uniqueLabel
      ? `A ${context.uniqueLabel} with that value already exists.`
      : 'That value already exists.';
  }
  if (code === '23503') {
    return context.fkLabel
      ? `Can't do that — ${context.fkLabel}.`
      : "Can't do that — this record is referenced by other data.";
  }
  if (code === '23502') return 'A required field is missing.';
  if (code === '23514') {
    return context.checkLabel
      ? `${context.checkLabel} — please choose a valid value.`
      : 'Value is outside the allowed range.';
  }
  if (code === '42501') return "You don't have permission to do that.";

  // PostgREST error codes (https://postgrest.org/en/stable/references/errors.html)
  if (code === 'PGRST116') return context.notFoundLabel || 'Not found.';
  if (code === 'PGRST301') return 'Your session has expired. Please log in again.';

  // GoTrue auth messages — match on the message text since they have no codes.
  if (/invalid login credentials/i.test(msg)) return 'Email or password is incorrect.';
  if (/email rate limit/i.test(msg)) return 'Too many requests. Please wait a few minutes and try again.';
  if (/email not confirmed/i.test(msg)) return 'Please confirm your email before logging in.';
  if (/user already registered/i.test(msg)) return 'An account with that email already exists.';
  if (/password should be at least/i.test(msg)) return 'Password is too short.';
  if (/auth session missing/i.test(msg)) return 'Your session has expired. Please log in again.';

  // Default — surface what the server said. Better than swallowing it silently.
  return msg || 'Something went wrong.';
}
